import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Tasks, Users, apiErrorMessage } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { TransitHoldBadge } from "../components/TransitHoldBadge";
import FilterBar from "../components/FilterBar";
import ExportButton from "../components/ExportButton";
import { defaultFilters, buildApiParams, deriveScope, deriveMonthLabel } from "../lib/filters";
import { fmtShortDateTime, fmtShortDateAndTime } from "../lib/auditFormat";
import { SALE_STATUS_LABELS } from "../lib/constants";
import { getRegistrationStage, REGISTRATION_STAGE } from "../lib/registrationStatus";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Eye, Printer, Phone, User as UserIcon,
  MapPin,
} from "lucide-react";

export default function SalesList() {
  const { activeLocationIds, locations, activeUser } = useApp();
  const [list, setList] = useState([]);
  const [tasksBySale, setTasksBySale] = useState({});
  const [usersById, setUsersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => ({
    ...defaultFilters(),
    status: null,
  }));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extra drill-down filters coming from the Reports page. These aren't
  // exposed in the FilterBar UI — they're passed straight through to the
  // backend so the list is scoped to whatever bucket the user tapped.
  const drilldown = useMemo(() => {
    const keys = ["buy_mode", "finance_company_id", "model", "district", "state", "location_id", "dse_id", "date_from", "date_to", "date_field"];
    const out = {};
    for (const k of keys) {
      const v = searchParams.get(k);
      if (v) out[k] = v;
    }
    return out;
  }, [searchParams]);

  const locParam = useMemo(() => {
    // If user picked a specific location in the FilterBar, narrow to that
    // (but only if it's within their allowed set). Otherwise fall back to
    // every location the user is currently viewing.
    if (filters.location_id && activeLocationIds.includes(filters.location_id)) {
      return filters.location_id;
    }
    return activeLocationIds.join(",");
  }, [activeLocationIds, filters.location_id]);
  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));

  // DSE list for the FilterBar — derived from cached users (loaded once below).
  const dseOptions = useMemo(
    () => Object.values(usersById)
      .filter((u) => u?.role === "DSE")
      .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [usersById],
  );
  // Visible locations the user is allowed to filter within.
  const locationOptions = useMemo(
    () => locations.filter((l) => activeLocationIds.includes(l.id)),
    [locations, activeLocationIds],
  );

  // Sale-status options consumed by FilterBar in this page.
  const saleStatusOptions = useMemo(
    () => Object.entries(SALE_STATUS_LABELS).map(([value, label]) => ({ value, label })),
    [],
  );

  useEffect(() => {
    setLoading(true);
    (async () => {
      const [data, allTasks, allUsers] = await Promise.all([
        Sales.list({
          location_ids: locParam,
          ...(activeUser?.role === "DSE" ? { dse_id: activeUser.id } : {}),
          ...(activeUser?.role === "SalesManager" ? { manager_id: activeUser.id } : {}),
          ...buildApiParams(filters),
          ...drilldown,
        }),
        Tasks.list({
          location_ids: locParam,
          with_sla: true,
          ...(activeUser?.role === "SalesManager" ? { manager_id: activeUser.id } : {}),
        }),
        Object.keys(usersById).length === 0
          ? Users.list().catch(() => [])
          : Promise.resolve(Object.values(usersById)),
      ]);
      setList(data);
      const grouped = {};
      for (const t of allTasks) {
        (grouped[t.sale_id] ||= []).push(t);
      }
      setTasksBySale(grouped);
      if (Array.isArray(allUsers) && allUsers.length) {
        const map = {};
        for (const u of allUsers) map[u.id] = u;
        setUsersById(map);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locParam, activeUser, filters, drilldown]);

  // Memoised CSV-export params — avoids a fresh object on every render.
  const exportParams = useMemo(() => ({
    location_ids: locParam,
    ...(activeUser?.role === "DSE" ? { dse_id: activeUser.id } : {}),
    ...buildApiParams(filters),
    ...drilldown,
  }), [locParam, activeUser, filters, drilldown]);

  const reload = async () => {
    const [data, allTasks] = await Promise.all([
      Sales.list({
        location_ids: locParam,
        ...(activeUser?.role === "DSE" ? { dse_id: activeUser.id } : {}),
        ...(activeUser?.role === "SalesManager" ? { manager_id: activeUser.id } : {}),
        ...buildApiParams(filters),
      }),
      Tasks.list({
        location_ids: locParam,
        with_sla: true,
        ...(activeUser?.role === "SalesManager" ? { manager_id: activeUser.id } : {}),
      }),
    ]);
    setList(data);
    const grouped = {};
    for (const t of allTasks) {
      (grouped[t.sale_id] ||= []).push(t);
    }
    setTasksBySale(grouped);
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this draft? This cannot be undone.")) return;
    try {
      await Sales.remove(id);
      toast.success("Draft deleted", {
        description: "The draft and its files have been removed.",
        duration: 2400,
      });
      await reload();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to delete draft");
    }
  };

  // Admin-only — destructive delete of a non-draft sale (any state).
  // Requires typing the SR number to confirm.
  const [adminDeleting, setAdminDeleting] = useState(null);  // sale doc | null
  const adminDelete = async (sale) => {
    try {
      await Sales.remove(sale.id);
      toast.success("Sale deleted", {
        description: `${sale.sr_number || sale.id} · all tasks and files removed.`,
        duration: 2400,
      });
      setAdminDeleting(null);
      await reload();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to delete sale");
    }
  };

  return (
    <div className="space-y-5" data-testid="sales-list-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            Sales Records
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">All Sales</h1>
        </div>
        {(activeUser?.role === "DSE" || activeUser?.role === "Admin") && (
          <Button onClick={() => navigate("/sales/new")} className="gap-2" data-testid="new-sale-btn">
            <Plus className="h-4 w-4" /> New Sale
          </Button>
        )}
      </div>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        onReset={() => setFilters(defaultFilters())}
        placeholder="Search SR / Customer / Mobile / VIN / Regn / Ledger"
        showStatus
        showSla={false}
        showLocation={locationOptions.length > 1}
        showDse={activeUser?.role !== "DSE" && dseOptions.length > 0}
        statusOptions={saleStatusOptions}
        locationOptions={locationOptions}
        dseOptions={dseOptions}
        rightSlot={
          <ExportButton
            endpoint="sales"
            params={exportParams}
            prefix={`Sales_${filters.status || "All"}`}
            scope={deriveScope({
              locFilter: filters.location_id,
              visibleLocs: locationOptions,
              allLocations: locations,
            })}
            monthLabel={deriveMonthLabel(filters)}
          />
        }
      />

      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500">Loading…</div>
        ) : list.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-slate-500">
            No sales found. Try changing filters or create a new sale.
          </div>
        ) : (
          <>
            {/* Desktop / tablet — table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3">Customer</th>
                    <th className="px-5 py-3">Vehicle</th>
                    <th className="px-5 py-3">Assigned To</th>
                    <th className="px-5 py-3">Delivery</th>
                    <th className="px-5 py-3">Progress</th>
                    <th className="px-5 py-3">SLA</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-white">
                  {list.map((s) => (
                    <SalesRowDesktop
                      key={s.id}
                      sale={s}
                      tasks={tasksBySale[s.id] || []}
                      locMap={locMap}
                      usersById={usersById}
                      role={activeUser?.role}
                      onOpen={() => navigate(`/sales/${s.id}`)}
                      onEdit={() => navigate(`/sales/${s.id}/edit`)}
                      onPrint={() => navigate(`/sales/${s.id}/print`)}
                      onDeleteDraft={() => deleteDraft(s.id)}
                      onAdminDelete={() => setAdminDeleting(s)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile — stacked cards */}
            <div className="md:hidden divide-y divide-slate-100">
              {list.map((s) => (
                <SalesCardMobile
                  key={s.id}
                  sale={s}
                  tasks={tasksBySale[s.id] || []}
                  locMap={locMap}
                  usersById={usersById}
                  role={activeUser?.role}
                  onOpen={() => navigate(`/sales/${s.id}`)}
                  onEdit={() => navigate(`/sales/${s.id}/edit`)}
                  onPrint={() => navigate(`/sales/${s.id}/print`)}
                  onDeleteDraft={() => deleteDraft(s.id)}
                  onAdminDelete={() => setAdminDeleting(s)}
                />
              ))}
            </div>
          </>
        )}
      </Card>

      <AdminDeleteSaleDialog
        sale={adminDeleting}
        onClose={() => setAdminDeleting(null)}
        onConfirm={() => adminDelete(adminDeleting)}
      />
    </div>
  );
}

function AdminDeleteSaleDialog({ sale, onClose, onConfirm }) {
  const [text, setText] = useState("");
  useEffect(() => { if (!sale) setText(""); }, [sale]);
  if (!sale) return null;
  const expected = sale.sr_number || sale.id;
  const canDelete = text.trim() === expected;
  const statusLabel = (sale.status || "").replace(/_/g, " ");
  return (
    <Dialog open={!!sale} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="admin-delete-sale-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <Trash2 className="h-5 w-5" />
            Delete Sale {expected}?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            Customer: <strong>{sale.customer?.name || "—"}</strong>{" · "}
            Status: <strong className="capitalize">{statusLabel}</strong>.
            Deleting will permanently remove the sale, all its tasks, payment receipts
            and soft-delete uploaded documents. <strong>This action cannot be undone.</strong>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              Type the SR number <span className="font-mono-data font-semibold text-rose-700">{expected}</span> to confirm
            </Label>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={expected}
              autoFocus
              data-testid="admin-delete-sale-confirm-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:hover:bg-slate-300"
            disabled={!canDelete}
            onClick={onConfirm}
            data-testid="admin-delete-sale-confirm-btn"
          >
            Delete Sale Permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STAGE_LABELS = {
  PDI_VIN: "Awaiting VIN Assignment",
  PDI_READY: "Vehicle Preparation",
  ACCOUNTS_INVOICE: "Tax Invoice Pending",
  INSURANCE: "Insurance Pending",
  ROAD_TAX: "Road Tax Pending",
  HSRP: "HSRP Pending",
  VAS_PP: "Protection Plus Pending",
  VAS_AMC: "AMC Pending",
  VAS_RSA: "RSA Pending",
  SPARES: "Spares · Accessories",
};
const STAGE_PRIORITY = [
  "PDI_VIN", "ACCOUNTS_INVOICE", "INSURANCE", "ROAD_TAX", "HSRP",
  "VAS_PP", "VAS_AMC", "VAS_RSA", "SPARES", "PDI_READY",
];

function StageLabel({ tasks, status, sale }) {
  // Detect Early Delivery state so we can append a unified subtext below
  // the stage badge: shows whenever the sale is going through (or has gone
  // through) the Early Delivery approval flow.
  const ed = sale?.early_delivery || {};
  const edSubtext = (() => {
    if (status === "delivered" && ed.delivery_type === "conditional")
      return "(Early Delivery)";
    if (ed.status === "approved")  return "(Early Delivery · Approved)";
    if (ed.status === "pending")   return "(Early Delivery · Pending)";
    if (ed.status === "rejected")  return "(Early Delivery · Rejected)";
    return null;
  })();
  const edToneCls = (() => {
    if (status === "delivered" && ed.delivery_type === "conditional")
      return "text-emerald-600";
    if (ed.status === "approved")  return "text-emerald-600";
    if (ed.status === "pending")   return "text-amber-700";
    if (ed.status === "rejected")  return "text-rose-600";
    return "text-slate-500";
  })();
  const withEdSubtext = (node) => (
    <div className="flex flex-col gap-0.5">
      {node}
      {edSubtext && (
        <span className={`text-[10px] font-semibold ${edToneCls}`}
              data-testid={`stage-early-delivery-${ed.status || (ed.delivery_type === 'conditional' ? 'conditional' : '')}`}>
          {edSubtext}
        </span>
      )}
    </div>
  );

  if (status === "draft") {
    return withEdSubtext(
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-semibold text-slate-600 w-fit">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
        Draft · Not Released
      </span>
    );
  }
  if (status === "delivered") {
    return withEdSubtext(
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 w-fit">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Delivered
      </span>
    );
  }
  if (status === "ready_for_delivery") {
    return withEdSubtext(
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 w-fit">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Ready for Delivery
      </span>
    );
  }
  const pending = tasks.filter((t) => t.status === "pending");
  if (pending.length === 0) {
    return withEdSubtext(<span className="text-xs text-slate-500">Released</span>);
  }
  const sorted = [...pending].sort(
    (a, b) => STAGE_PRIORITY.indexOf(a.type) - STAGE_PRIORITY.indexOf(b.type),
  );
  const primary = sorted[0];
  const extra = sorted.length - 1;

  // Collapse Road Tax + HSRP into the simplified 3-stage Registration label.
  if (primary.type === "ROAD_TAX" || primary.type === "HSRP") {
    const { stage } = getRegistrationStage(sale);
    return (
      <div className="flex flex-col gap-0.5">
        <span className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${stage.cls}`}
              data-testid={`reg-stage-${stage.key}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${stage.dot}`} />
          {stage.label}
        </span>
        {extra > 0 && (
          <span className="text-[10px] text-slate-500">+{extra} more in progress</span>
        )}
        {edSubtext && (
          <span className={`text-[10px] font-semibold ${edToneCls}`}
                data-testid={`stage-early-delivery-${ed.status || (ed.delivery_type === 'conditional' ? 'conditional' : '')}`}>
            {edSubtext}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800 w-fit">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        {STAGE_LABELS[primary.type] || primary.type}
      </span>
      {extra > 0 && (
        <span className="text-[10px] text-slate-500">
          +{extra} more in progress
        </span>
      )}
      {edSubtext && (
        <span className={`text-[10px] font-semibold ${edToneCls}`}
              data-testid={`stage-early-delivery-${ed.status || (ed.delivery_type === 'conditional' ? 'conditional' : '')}`}>
          {edSubtext}
        </span>
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Row & Card renderers (new compact design — see spec in chat history).
// Desktop = table row · Mobile = stacked card. Same data, two layouts.
// ───────────────────────────────────────────────────────────────────────────

const SLA_PRIORITY = { delayed: 4, at_risk: 3, on_hold: 2, paused: 1 };
const SLA_META = {
  on_track: { label: "On Track", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  at_risk:  { label: "At Risk",  cls: "border-amber-200 bg-amber-50 text-amber-800",       dot: "bg-amber-500" },
  delayed:  { label: "Delayed",  cls: "border-rose-200 bg-rose-50 text-rose-700",          dot: "bg-rose-500" },
  on_hold:  { label: "On Hold",  cls: "border-slate-200 bg-slate-50 text-slate-700",       dot: "bg-slate-500" },
  paused:   { label: "Paused",   cls: "border-slate-200 bg-slate-50 text-slate-700",       dot: "bg-slate-400" },
};

function worstSla(tasks) {
  let best = null;
  for (const t of tasks) {
    if (!t.sla || t.status === "completed" || t.status === "skipped") continue;
    const s = t.sla.status;
    if (!SLA_META[s]) continue;
    if (!best || (SLA_PRIORITY[s] || 0) > (SLA_PRIORITY[best.status] || 0)) {
      best = { status: s, remaining: t.sla.remaining_minutes };
    }
  }
  return best;
}

function fmtRemaining(min) {
  if (min == null) return "";
  const abs = Math.abs(min);
  const hrs = Math.floor(abs / 60);
  const mins = Math.round(abs % 60);
  const txt = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  return min < 0 ? `${txt} overdue` : `${txt} remaining`;
}

function SlaCell({ sla }) {
  if (!sla) return <span className="text-xs text-slate-400">—</span>;
  const meta = SLA_META[sla.status];
  return (
    <div className="flex flex-col gap-1" data-testid={`sla-cell-${sla.status}`}>
      <span className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.cls}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
        {meta.label}
      </span>
      {sla.remaining != null && (
        <span className="text-[11px] text-slate-500">{fmtRemaining(sla.remaining)}</span>
      )}
    </div>
  );
}

function deliveryBadge(sale) {
  const d = sale.delivery?.promised_date;
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  if (Number.isNaN(target.getTime())) return null;
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (sale.status === "delivered") {
    return { label: "Delivered", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  }
  if (diff < 0) return { label: "Overdue",  cls: "border-rose-200 bg-rose-50 text-rose-700" };
  if (diff === 0) return { label: "Today",   cls: "border-amber-200 bg-amber-50 text-amber-800" };
  if (diff === 1) return { label: "Tomorrow",cls: "border-amber-200 bg-amber-50 text-amber-800" };
  return { label: "Upcoming", cls: "border-slate-200 bg-slate-50 text-slate-700" };
}

function ActionIcons({ sale, role, onOpen, onEdit, onPrint, onDeleteDraft, onAdminDelete, compact = false }) {
  const phone = sale.customer?.phone;
  const isDraft = sale.status === "draft";
  const canEdit = role === "Admin" || role === "DSE";
  const canDeleteDraft = isDraft && (role === "DSE" || role === "Admin");
  const canAdminDelete = !isDraft && role === "Admin";
  const baseBtn = "rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors";
  const size = compact ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
      <button
        type="button" title="View" onClick={onOpen}
        className={baseBtn} data-testid={`view-${sale.id}`}
      >
        <Eye className={size} />
      </button>
      {canEdit && (
        <button
          type="button" title={isDraft ? "Resume draft" : "Edit"}
          onClick={onEdit}
          className={baseBtn} data-testid={`edit-${sale.id}`}
        >
          <Pencil className={size} />
        </button>
      )}
      {!isDraft && (
        <button
          type="button" title="Print" onClick={onPrint}
          className={baseBtn} data-testid={`print-${sale.id}`}
        >
          <Printer className={size} />
        </button>
      )}
      {phone && (
        <a
          href={`tel:${phone}`} title={`Call ${phone}`}
          onClick={(e) => e.stopPropagation()}
          className={baseBtn} data-testid={`call-${sale.id}`}
        >
          <Phone className={size} />
        </a>
      )}
      {canDeleteDraft && (
        <button
          type="button" title="Delete draft" onClick={onDeleteDraft}
          className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
          data-testid={`delete-draft-row-${sale.id}`}
        >
          <Trash2 className={size} />
        </button>
      )}
      {canAdminDelete && (
        <button
          type="button" title="Delete sale (Admin)" onClick={onAdminDelete}
          className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
          data-testid={`delete-sale-row-${sale.id}`}
        >
          <Trash2 className={size} />
        </button>
      )}
    </div>
  );
}

function CustomerCell({ sale }) {
  const createdLabel = fmtShortDateTime(sale.created_at);
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="font-semibold text-slate-900 truncate">
        {sale.customer?.name || "—"}
      </span>
      {sale.customer?.phone && (
        <span className="text-xs text-slate-500 tabular-nums">{sale.customer.phone}</span>
      )}
      <div className="flex items-center gap-1.5">
        {sale.sr_number ? (
          <Link
            to={`/sales/${sale.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono-data text-[11px] font-semibold text-primary hover:underline"
          >
            {sale.sr_number}
          </Link>
        ) : (
          <span className="text-[11px] text-slate-400">— Draft —</span>
        )}
        {sale.transit_hold?.active && <TransitHoldBadge />}
      </div>
      {createdLabel && (
        <span
          className="font-mono-data text-[10px] uppercase tracking-wider text-slate-400"
          data-testid={`sales-row-created-${sale.id}`}
        >
          Created · {createdLabel}
        </span>
      )}
    </div>
  );
}

function VehicleCell({ sale, locMap }) {
  const code = locMap[sale.location_id]?.code;
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-slate-900 truncate">{sale.vehicle?.model || "—"}</span>
      <span className="text-xs text-slate-500 truncate font-mono-data">
        {sale.vehicle?.vin ? `VIN ${sale.vehicle.vin}` : "VIN pending"}
      </span>
      {code && (
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
          <MapPin className="h-3 w-3 text-slate-400" />
          {code}
        </span>
      )}
    </div>
  );
}

function AssignedCell({ sale, usersById }) {
  const name = usersById[sale.dse_id]?.name || "Unassigned";
  return (
    <div className="flex items-center gap-1.5 text-sm text-slate-700">
      <UserIcon className="h-3.5 w-3.5 text-slate-400" />
      <span className="truncate">{name}</span>
    </div>
  );
}

function DeliveryCell({ sale }) {
  const badge = deliveryBadge(sale);
  const txt = fmtShortDateAndTime(sale.delivery?.promised_date, sale.delivery?.promised_time);
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span
        className="font-mono-data text-sm text-slate-700"
        data-testid={`sales-row-delivery-${sale.id}`}
      >
        {txt || "—"}
      </span>
      {badge && (
        <span className={`inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${badge.cls}`}>
          {badge.label}
        </span>
      )}
    </div>
  );
}

function SalesRowDesktop({ sale, tasks, locMap, usersById, role, onOpen, onEdit, onPrint, onDeleteDraft, onAdminDelete }) {
  const sla = worstSla(tasks);
  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-slate-50"
      onClick={onOpen}
      data-testid={`sales-row-${sale.id}`}
    >
      <td className="px-5 py-3 align-top"><CustomerCell sale={sale} /></td>
      <td className="px-5 py-3 align-top"><VehicleCell sale={sale} locMap={locMap} /></td>
      <td className="px-5 py-3 align-top"><AssignedCell sale={sale} usersById={usersById} /></td>
      <td className="px-5 py-3 align-top"><DeliveryCell sale={sale} /></td>
      <td className="px-5 py-3 align-top">
        <StageLabel tasks={tasks} status={sale.status} sale={sale} />
      </td>
      <td className="px-5 py-3 align-top"><SlaCell sla={sla} /></td>
      <td className="px-2 py-3 align-top">
        <ActionIcons
          sale={sale} role={role}
          onOpen={onOpen} onEdit={onEdit} onPrint={onPrint}
          onDeleteDraft={onDeleteDraft} onAdminDelete={onAdminDelete}
          compact
        />
      </td>
    </tr>
  );
}

function SalesCardMobile({ sale, tasks, locMap, usersById, role, onOpen, onEdit, onPrint, onDeleteDraft, onAdminDelete }) {
  const sla = worstSla(tasks);
  const dBadge = deliveryBadge(sale);
  return (
    <div
      className="bg-white px-4 py-3 active:bg-slate-50 transition-colors"
      onClick={onOpen}
      data-testid={`sales-card-${sale.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 truncate">
              {sale.customer?.name || "—"}
            </span>
            {sale.sr_number ? (
              <span className="font-mono-data text-[11px] font-semibold text-primary">
                {sale.sr_number}
              </span>
            ) : (
              <span className="text-[11px] text-slate-400">— Draft —</span>
            )}
            {sale.transit_hold?.active && <TransitHoldBadge />}
          </div>
          {sale.customer?.phone && (
            <span className="text-xs text-slate-500 tabular-nums">{sale.customer.phone}</span>
          )}
          {fmtShortDateTime(sale.created_at) && (
            <span className="font-mono-data text-[10px] uppercase tracking-wider text-slate-400">
              Created · {fmtShortDateTime(sale.created_at)}
            </span>
          )}
        </div>
        {sla && (
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${SLA_META[sla.status].cls}`}
            data-testid={`sla-card-${sla.status}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${SLA_META[sla.status].dot}`} />
            {SLA_META[sla.status].label}
          </span>
        )}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <Meta label="Vehicle">
          <span className="text-slate-700 truncate">{sale.vehicle?.model || "—"}</span>
          {locMap[sale.location_id]?.code && (
            <span className="ml-1 text-slate-500">· {locMap[sale.location_id]?.code}</span>
          )}
        </Meta>
        <Meta label="DSE">
          <span className="inline-flex items-center gap-1 text-slate-700">
            <UserIcon className="h-3 w-3 text-slate-400" />
            {usersById[sale.dse_id]?.name || "Unassigned"}
          </span>
        </Meta>
        <Meta label="Promised">
          <span className="font-mono-data text-slate-700">
            {fmtShortDateAndTime(sale.delivery?.promised_date, sale.delivery?.promised_time) || "—"}
          </span>
          {dBadge && (
            <span className={`ml-1.5 inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold ${dBadge.cls}`}>
              {dBadge.label}
            </span>
          )}
        </Meta>
        <Meta label="Stage">
          <CompactStage tasks={tasks} status={sale.status} sale={sale} />
        </Meta>
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-3 border-t border-slate-100 pt-2">
        {sla?.remaining != null ? (
          <span className="text-[11px] text-slate-500">{fmtRemaining(sla.remaining)}</span>
        ) : <span />}
        <ActionIcons
          sale={sale} role={role}
          onOpen={onOpen} onEdit={onEdit} onPrint={onPrint}
          onDeleteDraft={onDeleteDraft} onAdminDelete={onAdminDelete}
        />
      </div>
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </span>
      <span className="text-xs text-slate-700 truncate">{children}</span>
    </div>
  );
}

// Compact stage label used in the mobile card (one-liner, no extra count line).
function CompactStage({ tasks, status, sale }) {
  const ed = sale?.early_delivery || {};
  const edSubtext = (() => {
    if (status === "delivered" && ed.delivery_type === "conditional")
      return "(Early Delivery)";
    if (ed.status === "approved")  return "(Early Delivery · Approved)";
    if (ed.status === "pending")   return "(Early Delivery · Pending)";
    if (ed.status === "rejected")  return "(Early Delivery · Rejected)";
    return null;
  })();
  const edToneCls = (() => {
    if (status === "delivered" && ed.delivery_type === "conditional")
      return "text-emerald-600";
    if (ed.status === "approved")  return "text-emerald-600";
    if (ed.status === "pending")   return "text-amber-700";
    if (ed.status === "rejected")  return "text-rose-600";
    return "text-slate-500";
  })();
  const wrap = (main) => (
    <div className="flex flex-col gap-0.5 min-w-0">
      {main}
      {edSubtext && (
        <span className={`text-[10px] font-semibold ${edToneCls} truncate`}>
          {edSubtext}
        </span>
      )}
    </div>
  );

  if (status === "draft") return wrap(<span className="text-slate-500 truncate">Draft</span>);
  if (status === "delivered") return wrap(<span className="text-emerald-700 font-semibold truncate">Delivered</span>);
  if (status === "ready_for_delivery") return wrap(<span className="text-emerald-700 font-semibold truncate">Ready</span>);
  const pending = tasks.filter((t) => t.status === "pending");
  if (pending.length === 0) return wrap(<span className="text-slate-500 truncate">Released</span>);
  const sorted = [...pending].sort(
    (a, b) => STAGE_PRIORITY.indexOf(a.type) - STAGE_PRIORITY.indexOf(b.type),
  );
  const primary = sorted[0];
  // Collapse Road Tax / HSRP into 3-stage Registration label
  if (primary.type === "ROAD_TAX" || primary.type === "HSRP") {
    const { stage } = getRegistrationStage(sale);
    return wrap(
      <span className={`truncate font-medium ${stage.tone === "emerald" ? "text-emerald-700"
        : stage.tone === "indigo" ? "text-indigo-700"
        : "text-amber-800"}`}>
        {stage.label}
        {sorted.length > 1 && (
          <span className="ml-1 text-slate-400">+{sorted.length - 1}</span>
        )}
      </span>
    );
  }
  return wrap(
    <span className="text-amber-800 font-medium truncate">
      {STAGE_LABELS[primary.type] || primary.type}
      {sorted.length > 1 && (
        <span className="ml-1 text-slate-400">+{sorted.length - 1}</span>
      )}
    </span>
  );
}
